import 'photoswipe/dist/photoswipe.css'
import 'photoswipe/dist/default-skin/default-skin.css'

import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo } from 'react'
import LazyLoad from 'react-lazyload'
import { Item } from 'react-photoswipe-gallery'
import Fade from 'react-reveal/Fade'

export interface Props {
  imagesNumber?: number
  isLast?: boolean
  languageCode: string
  original: string
  thumbnail?: string
  title?: string
  width: string
  height: string
}

export const Image = memo(function Image({
  imagesNumber,
  isLast,
  languageCode,
  original,
  thumbnail,
  title,
  width,
  height,
}: Props) {
  return (
    <Container>
      <Fade bottom distance="3.75rem" duration={600}>
        <Item
          original={original}
          thumbnail={thumbnail}
          title={title}
          width={width}
          height={height}
        >
          {({ ref, open }) => (
            <>
              <Inner
                ref={ref as React.RefObject<HTMLDivElement>}
                onClick={open}
                style={{
                  backgroundImage: `url(${thumbnail})`,
                }}
              />
              {isLast && imagesNumber && imagesNumber > 4 ? (
                <ImagesNumber dial={5} onClick={open}>
                  <SVG />
                  <ImagesNumberLabel>
                    {`${imagesNumber - 4} ${useVocabularyData(
                      'images',
                      languageCode,
                    )}`}
                  </ImagesNumberLabel>
                </ImagesNumber>
              ) : null}
            </>
          )}
        </Item>
      </Fade>
    </Container>
  )
})

const Container = styled.div`
  display: none;
  width: calc(67% - 1px);
  cursor: pointer;
  margin-bottom: 2px;
  padding-bottom: 33%;
  position: relative;
  &:nth-of-type(-n + 4) {
    display: block;
  }
  &:nth-of-type(1),
  &:nth-of-type(4) {
    width: calc(33% - 1px);
  }
  > div,
  > div > div {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`

const ImagesNumber = styled(FlexBox)`
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  position: absolute;
  top: 0;
  left: 0;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.4;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const ImagesNumberLabel = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.75rem;
  letter-spacing: 1px;
  line-height: 0.9375rem;
  position: relative;
  text-transform: lowercase;
`

const SVG = styled.div`
  width: 36px;
  height: 36px;
  margin-bottom: 1rem;
  position: relative;
  &:before,
  &:after {
    content: '';
    background: ${({ theme }) => theme.colors.variants.neutralLight4};
    position: absolute;
  }
  &:before {
    width: 2px;
    height: 100%;
    top: 0;
    left: calc(50% - 2px);
  }
  &:after {
    width: 100%;
    height: 2px;
    top: calc(50% - 2px);
    left: 0;
  }

  @media (max-width: 767px) {
    width: 1.5rem;
    height: 1.5rem;
  }
`
